import * as Types from "../shared/Types";
import {
  ingredientDryYeast,
  ingredientFlour,
  ingredientLevain,
  ingredientUnknown,
  ingredientWater,
  LEVAIN_PCT_TYPE_TOTAL_WT,
  noPreferment
} from "../shared/Types";
import {convertUnitToGrams, getUnitLabel, unitGrams, unitTeaspoon} from "../shared/Units";
import {toFixedDecimal, toFixedWholePercent,} from "../utils/NumberUtils";
import {yeastDefs} from "../shared/Yeast";
import {convertToRatioForm} from "./WeightToRatioConverter";
import {findObjectForFieldValue, getSumForField} from "../utils/ArrayUtils";
import {getLiquidContributingInclusionWeight} from "../shared/IngredientSupport";
import {
  calculateIngredientCosts,
  getAllIngredientSummary,
  getPrefermentRefreshBuildComponents
} from "../dough/CalculatorUtils";

const weightRecipeItems = {
  formName: '',
  formType: '',
  attribution: '',
  createdAt: '',
  modifiedAt: '',
  notes: '',
  numBatches: 1,
  batchSize: 0,
  batchType: Types.BATCH_TYPE_TOTAL_WT,
  includePreferment: false,
  pffBakersPct: false,
  bakersPercentFlourWeight: 0,
  recipeHydrationPct: 0,
  effectiveRecipeFlourTotalWeight: 0,
  effectiveRecipeLiquidTotalWeight: 0,
  effectiveRecipeHydrationPct: 0,
  recipeFlourTotalWeight: 0,
  recipeLiquidTotalWeight: 0,
  recipeYeastTotalWeight: 0,
  recipeStarterTotalWeight: 0,
  recipeMixinTotalWeight: 0,
  recipeSubmixTotalWeight: 0,
  recipeUnitWeight: 0,
  recipeTotalWeight: 0,
  ingredientSummaries: {},
  costData: [],
  ingredientCosts: [],
  mainDough: {
    hydrationPct: 0,
    hydrationPctFraction: 0,
    process: {},
    prefermentPffPct: 0,
    prefermentPffPctFraction: 0,
    prefermentPffPctType: LEVAIN_PCT_TYPE_TOTAL_WT,
    starterType: LEVAIN_PCT_TYPE_TOTAL_WT,
    starterPct: 0,
    starterPctFraction: 0,
    starterFlourPercent: 0,
    starterLiquidPercent: 0,
    starterHydrationPct: 0,
    starterHydrationPctFraction: 0,
    yeast: {
      flourUnitGrams: yeastDefs.flourUnitGrams,
      ratioId: 0,
      percent: 0,
      weight: 0,
      altUnit: unitTeaspoon,
      altUnitAmount: 0,
      volumeDisplayLabel: ''
    },
    starterWeight: 0,
    starterAltUnit: unitGrams,
    starterAltUnitAmount: 0,
    flourWeight: 0,
    flour: [],
    liquid: [],
    mixin: [],
    submixes: [],
    toppings: [],
    totalWeight: 0,
    prefermentByWeightPct: 0
  },
  prefermentDough: {
    prefermentType: noPreferment,
    hydrationPct: 0,
    hydrationPctFraction: 0,
    levainFermPct: 0,
    levainFermTempF: '0',
    levainFermTimeStr: '',
    starterType: LEVAIN_PCT_TYPE_TOTAL_WT,
    starterPct: 0,
    starterPctFraction: 0,
    starterHydrationPct: 0,
    starterHydrationPctFraction: 0,
    yeast: {
      flourUnitGrams: yeastDefs.flourUnitGrams,
      ratioId: 0,
      percent: 0,
      weight: 0,
      altUnit: unitTeaspoon,
      altUnitAmount: 0,
      volumeDisplayLabel: ''
    },
    starterWeight: 0,
    starterAltUnit: unitGrams,
    starterAltUnitAmount: 0,
    addBuffer: false,
    flourWeight: 0,
    flourEffectiveWeight: 0,
    flour: [],
    liquidWeight: 0,
    liquidEffectiveWeight: 0,
    liquid: [],
    mixin: [],
    mixinWeight: 0,
    totalWeight: 0
  }
}

function calculateWeightRecipe(inputData) {
  // get basic percentages to be applied in output items
  const ratioItems = convertToRatioForm(inputData)
  // initialize the recipe items as a copy
  let items = JSON.parse(JSON.stringify(weightRecipeItems))
  items.formName = inputData.formName
  items.formType = inputData.formType
  items.numBatches = inputData.numBatches
  items.attribution = inputData.attribution
  items.createdAt = inputData.createdAt
  items.modifiedAt = inputData.modifiedAt
  items.notes = inputData.notes
  items.includePreferment = inputData.includePreferment
  // alt units...
  items.mainDough.starterAltUnit = inputData.starterUnit
  items.mainDough.starterAltUnitAmount = inputData.starterAmount
  items.mainDough.process = inputData.process ?? {}

  // add alt unit and amount to ratio-calculated flours and push to recipe
  let mainDoughFlourWeight = 0
  ratioItems.flour.forEach((fd, ix) => {
    const inputFlour = findObjectForFieldValue(inputData.flour, 'id', fd.id)
    const flourWeight = Math.round(convertUnitToGrams(inputFlour.amount, inputFlour.unit, ingredientFlour))
    mainDoughFlourWeight += flourWeight
    const flourItem = {...fd, weight: flourWeight, group: 'Flour', altUnit: inputFlour.unit, altUnitAmount: inputFlour.amount}
    items.mainDough.flour.push(flourItem)
  })

  // add alt unit and amount to ratio-calculated liquids and push to recipe
  let mainDoughLiquidWeight = 0
  ratioItems.liquid.forEach((ld, ix) => {
    const inputLiquid = findObjectForFieldValue(inputData.liquid, 'id', ld.id)
    const liquidWeight = Math.round(convertUnitToGrams(inputLiquid.amount, inputLiquid.unit, ingredientWater))
    mainDoughLiquidWeight += liquidWeight
    const liquidItem = {...ld, weight: liquidWeight, group: 'Liquid', altUnit: inputLiquid.unit, altUnitAmount: inputLiquid.amount}
    items.mainDough.liquid.push(liquidItem)
  })

  items.mainDough.submixes = [...ratioItems.submixes]
  let recipeSubmixWeight = getSumForField(items.mainDough.submixes, 'weight')

  // add alt unit and amount to ratio-calculated mixins and push to recipe
  let mainMixinWeight = 0
  ratioItems.mixin.forEach((md, ix) => {
    const inputMixin = findObjectForFieldValue(inputData.mixin, 'id', md.id)
    const mixinWeight = Math.round(convertUnitToGrams(inputMixin.amount, inputMixin.unit, ingredientUnknown))
    mainMixinWeight += mixinWeight
    items.mainDough.mixin.push({...md, weight: mixinWeight, altUnit: inputMixin.unit, altUnitAmount: inputMixin.amount})
  })

  // toppings is just a category of mixin but weight isn't included
  ratioItems.toppings.forEach((td, ix) => {
    const inputTopping = findObjectForFieldValue(inputData.toppings, 'id', td.id)
    items.mainDough.toppings.push({...td, weight: 0, group: 'Toppings', altUnit: inputTopping.unit, altUnitAmount: inputTopping.amount})
  })


  // main dough percentages and weights...
  items.mainDough.hydrationPct = ratioItems.hydrationPct
  items.mainDough.starterPct = ratioItems.starterPct
  items.mainDough.starterHydrationPct = ratioItems.starterHydrationPct
  items.mainDough.yeast = calculateYeastForWeight(mainDoughFlourWeight, inputData.yeastAmount, inputData.yeastUnit)
  items.mainDough.yeast.ratioId = ratioItems.yeastRatioId
  items.mainDough.starterWeight = Math.round(convertUnitToGrams(inputData.starterAmount, inputData.starterUnit, ingredientLevain))
  items.mainDough.starterFlourWeight = ratioItems.starterFlourWeight
  items.mainDough.starterLiquidWeight = ratioItems.starterLiquidWeight
  items.mainDough.starterFlourPercent = ratioItems.starterFlourPercent
  items.mainDough.starterLiquidPercent = ratioItems.starterLiquidPercent
  items.mainDough.flourWeight = mainDoughFlourWeight
  items.mainDough.totalWeight = Math.round(mainDoughFlourWeight + mainDoughLiquidWeight + mainMixinWeight + recipeSubmixWeight + items.mainDough.starterWeight + items.mainDough.yeast.weight)

  let prefermentFlourWeight = 0
  let prefermentLiquidWeight = 0
  let prefermentMixinWeight = 0
  let prefermentYeastWeight = 0
  let prefermentStarterWeight = 0
  let prefermentTotalWeight = 0
  if (inputData.includePreferment) {
    items.prefermentDough.prefermentType = inputData.prefermentData.prefermentType
    // alt units...
    items.prefermentDough.starterAltUnit = inputData.prefermentData.starterUnit
    items.prefermentDough.starterAltUnitAmount = inputData.prefermentData.starterAmount
    // add alt unit and amount to ratio-calculated flours and push to recipe
    ratioItems.prefermentData.flour.forEach((fd, ix) => {
      const inputFlour = findObjectForFieldValue(inputData.prefermentData.flour, 'id', fd.id)
      const flourWeight = Math.round(convertUnitToGrams(inputFlour.amount, inputFlour.unit, ingredientFlour))
      prefermentFlourWeight += flourWeight
      items.prefermentDough.flour.push({...fd, weight: flourWeight, group: 'Flour', altUnit: inputFlour.unit, altUnitAmount: inputFlour.amount})
    })
    // add alt unit and amount to ratio-calculated liquids and push to recipe
    ratioItems.prefermentData.liquid.forEach((ld, ix) => {
      const inputLiquid = findObjectForFieldValue(inputData.prefermentData.liquid, 'id', ld.id)
      const liquidWeight = Math.round(convertUnitToGrams(inputLiquid.amount, inputLiquid.unit, ingredientWater))
      prefermentLiquidWeight += liquidWeight
      items.prefermentDough.liquid.push({...ld, weight: liquidWeight, group: 'Liquid', altUnit: inputLiquid.unit, altUnitAmount: inputLiquid.amount})
    })

    ratioItems.prefermentData.mixin.forEach((md, ix) => {
      const inputMixin = findObjectForFieldValue(inputData.prefermentData.mixin, 'id', md.id)
      const mixinWeight = Math.round(convertUnitToGrams(inputMixin.amount, inputMixin.unit, ingredientUnknown))
      prefermentMixinWeight += mixinWeight
      items.prefermentDough.mixin.push({...md, weight: mixinWeight, group: 'Inclusion', altUnit: inputMixin.unit, altUnitAmount: inputMixin.amount})
    })

    // preferment percentages and weights...
    items.mainDough.prefermentPffPct = ratioItems.prefermentPffPct
    items.mainDough.prefermentPffPctType = ratioItems.prefermentPffPctType
    items.prefermentDough.addBuffer = ratioItems.prefermentData.addBuffer
    items.prefermentDough.hydrationPct = ratioItems.prefermentData.hydrationPct
    items.prefermentDough.starterPct = ratioItems.prefermentData.starterPct
    items.prefermentDough.starterHydrationPct = ratioItems.prefermentData.starterHydrationPct

    items.prefermentDough.yeast = calculateYeastForWeight(prefermentFlourWeight, inputData.prefermentData.yeastAmount, inputData.prefermentData.yeastUnit)
    items.prefermentDough.yeast.ratioId = ratioItems.prefermentData.yeastRatioId
    prefermentYeastWeight = items.prefermentDough.yeast.weight
    // not setting starterFlourWeight and starterLiquidWeight since Weight form is by levain total weight only
    prefermentStarterWeight = Math.round(convertUnitToGrams(inputData.prefermentData.starterAmount, inputData.prefermentData.starterUnit, ingredientLevain))
    items.prefermentDough.starterWeight = prefermentStarterWeight
    items.prefermentDough.flourWeight = prefermentFlourWeight
    items.prefermentDough.liquidWeight = prefermentLiquidWeight
    prefermentTotalWeight = Math.round(prefermentFlourWeight + prefermentLiquidWeight + prefermentMixinWeight + items.prefermentDough.starterWeight + items.prefermentDough.yeast.weight)
    items.prefermentDough.totalWeight = prefermentTotalWeight
    items.prefermentDough.flourEffectiveWeight = ratioItems.prefermentData.flourEffectiveWeight
    items.mainDough.prefermentByWeightPct = toFixedWholePercent(prefermentTotalWeight / ratioItems.bakersPercentFlourWeight, 2)
  }
  // calculate all recipe weights
  items.recipeFlourTotalWeight = mainDoughFlourWeight + prefermentFlourWeight
  items.bakersPercentFlourWeight = ratioItems.bakersPercentFlourWeight
  items.effectiveRecipeFlourTotalWeight = ratioItems.effectiveRecipeFlourTotalWeight
  items.effectiveRecipeHydrationPct = ratioItems.effectiveRecipeHydrationPct
  items.recipeHydrationPct = ratioItems.recipeHydrationPct
  items.recipeLiquidTotalWeight = mainDoughLiquidWeight + prefermentLiquidWeight
  items.effectiveRecipeHydrationPct = ratioItems.effectiveRecipeHydrationPct
  items.effectiveRecipeLiquidTotalWeight = ratioItems.effectiveRecipeLiquidTotalWeight
  items.recipeYeastTotalWeight = toFixedDecimal(items.mainDough.yeast.weight + prefermentYeastWeight, 1)
  items.recipeStarterTotalWeight = items.mainDough.starterWeight + prefermentStarterWeight
  items.recipeMixinTotalWeight = mainMixinWeight + prefermentMixinWeight
  items.recipeSubmixTotalWeight = recipeSubmixWeight

  const allIngredientSummaries = getAllIngredientSummary(items)
  items.ingredientSummaries = allIngredientSummaries
  items.ingredientCosts = calculateIngredientCosts(allIngredientSummaries, items.costData, items.numBatches)

  let liquidContributingIngredientWeight = getLiquidContributingInclusionWeight(allIngredientSummaries.inclusion)
  items.effectiveRecipeLiquidTotalWeight += liquidContributingIngredientWeight
  items.effectiveRecipeHydrationPct = toFixedDecimal(100 * (items.effectiveRecipeLiquidTotalWeight / items.effectiveRecipeFlourTotalWeight))


  items.recipeTotalWeight = items.mainDough.totalWeight + items.prefermentDough.totalWeight
  items.recipeUnitWeight = Math.round(items.recipeTotalWeight / items.numBatches)
  items.batchSize = items.recipeUnitWeight / items.numBatches

  // generate levain two-step refresh build sequence: first at original seed%, second at 100% seed
  //  (only applicable for levains that are just flour, water and starter - exclude levains with inclusions)
  if (items.includePreferment && items.prefermentDough.starterWeight > 0 && items.prefermentDough.mixin.length === 0) {
    const refreshComp = getPrefermentRefreshBuildComponents(prefermentTotalWeight, 0,
            (items.prefermentDough.starterPct / 100), (items.prefermentDough.starterHydrationPct / 100), (items.prefermentDough.hydrationPct / 100), [])
    items.prefermentDough.refresh1 = refreshComp.refresh1
    items.prefermentDough.refresh2 = refreshComp.refresh2
  }

  return items
}

function calculateYeastForWeight(flourWeight, yeastAltUnitAmount, yeastAltUnit) {
  // early return if 0 yeast amount?
  if (yeastAltUnitAmount <= 0) {
    return {weight: 0, percent: 0, altUnit: yeastAltUnit, altUnitAmount: yeastAltUnitAmount }
  }
  let yeastWeight = toFixedDecimal(convertUnitToGrams(yeastAltUnitAmount, yeastAltUnit, ingredientDryYeast), 1)
  let displayLabel = `${yeastAltUnitAmount} ${getUnitLabel(yeastAltUnit)}`
  return {
    weight: yeastWeight,
    percent: toFixedWholePercent(yeastWeight / flourWeight),
    altUnit: yeastAltUnit,
    altUnitAmount: yeastAltUnitAmount,
    volumeDisplayLabel: displayLabel
  }
}

export {calculateWeightRecipe}